import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays } from 'date-fns';
import { CalendarPlus, Clock, CalendarCheck } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { formatDisplayDate, parseApiDate } from '@/shared/utils/dateUtils';
import { useMyTimeOffBalance } from '../../hooks/useMyTimeOffBalance';
import { useMyTimeOffSummary } from '../../hooks/useMyTimeOffSummary';
import { formatDayRange, formatLongDate } from './dateRangeFormat';
import type { TimeOffRequestDto } from '../../types';

interface TimeOffOverviewBarProps {
  /** Already-fetched requests from the parent calendar view — avoids a second network call. */
  requests: TimeOffRequestDto[];
  typeNameById: Map<string, string>;
}

const SEPARATOR = (
  <div className="hidden h-10 w-px shrink-0 bg-border sm:block" aria-hidden="true" />
);

export function TimeOffOverviewBar({ requests, typeNameById }: TimeOffOverviewBarProps) {
  const { t } = useTranslation('timeOff');
  const balance = useMyTimeOffBalance();
  const summary = useMyTimeOffSummary();

  if (balance.isLoading || summary.isLoading) {
    return (
      <div
        className="flex flex-wrap items-center gap-6 border-b pb-4"
        data-testid="time-off-overview-loading-skeleton"
      >
        <Skeleton className="h-16 w-40" />
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-10 w-48" />
      </div>
    );
  }

  if (balance.isError || !balance.data) {
    return (
      <p
        className="border-b pb-4 text-center text-sm text-muted-foreground"
        data-testid="time-off-overview-empty-state"
      >
        {t('balance.empty')}
      </p>
    );
  }

  const { availableDays, usedDays, entitledDays, endDate, startDate } = balance.data;
  const usedPercent =
    entitledDays > 0 ? Math.min(100, Math.round((usedDays / entitledDays) * 100)) : 0;

  const nextAbsenceDate = summary.data?.nextAbsenceDate ?? null;
  const nextAbsenceRequest = nextAbsenceDate
    ? requests.find((r) => r.days?.some((d) => d.date.slice(0, 10) === nextAbsenceDate))
    : undefined;
  const nextAbsenceRange = nextAbsenceRequest?.days?.length
    ? formatDayRange(nextAbsenceRequest.days.map((d) => d.date))
    : nextAbsenceDate
      ? formatDisplayDate(nextAbsenceDate)
      : '';
  const daysUntilNextAbsence = nextAbsenceDate
    ? differenceInCalendarDays(parseApiDate(nextAbsenceDate)!, new Date())
    : null;

  const nextPending = requests
    .filter((r) => r.status === 'Pending')
    .sort((a, b) => (a.days?.[0]?.date ?? '').localeCompare(b.days?.[0]?.date ?? ''))[0];

  return (
    <div
      className="flex flex-wrap items-center gap-x-6 gap-y-4 border-b pb-4"
      data-testid="time-off-overview-card"
    >
      <div className="min-w-[10rem]">
        <div className="flex items-baseline gap-1.5">
          <span className="text-4xl font-bold tracking-tight text-emerald-600 dark:text-emerald-400">
            {availableDays}
          </span>
          <span className="text-sm text-muted-foreground">
            {t('calendar.overview.daysAvailable')}
          </span>
        </div>
        <div
          className="mt-2 h-1.5 w-full max-w-40 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={usedPercent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full bg-emerald-600 dark:bg-emerald-400"
            style={{ width: `${usedPercent}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('calendar.overview.usedOfEntitled', {
            used: usedDays,
            entitled: entitledDays,
            date: formatDisplayDate(endDate),
          })}
        </p>
      </div>

      {nextAbsenceDate && (
        <>
          {SEPARATOR}
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              {t('calendar.overview.nextVacation')}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <CalendarPlus className="size-4 text-muted-foreground" aria-hidden="true" />
              <span className="text-sm font-medium">{nextAbsenceRange}</span>
              {daysUntilNextAbsence !== null && (
                <span className="text-xs text-muted-foreground">
                  · {t('calendar.overview.inDays', { count: daysUntilNextAbsence })}
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {summary.data && summary.data.pendingRequestCount > 0 && (
        <>
          {SEPARATOR}
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              {t('calendar.overview.requests')}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <Clock className="size-4 text-amber-600 dark:text-amber-400" aria-hidden="true" />
              <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                {t('calendar.overview.pendingCount', { count: summary.data.pendingRequestCount })}
              </span>
              {nextPending?.days?.[0] && (
                <span className="text-xs text-muted-foreground">
                  · {typeNameById.get(nextPending.typeId ?? '') ?? ''},{' '}
                  {formatDayRange([nextPending.days[0].date])}
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {/* `startDate` is the *current cycle's* start, not the member's original
          hire date — no endpoint returns that (see tasks/current-story.md,
          "Extensión: Mi calendario", requerimiento de backend pendiente). Shown
          as the closest available stand-in until that endpoint exists. */}
      {startDate && (
        <>
          {SEPARATOR}
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              {t('calendar.overview.hireDate')}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <CalendarCheck className="size-4 text-muted-foreground" aria-hidden="true" />
              <span className="text-sm font-medium">{formatLongDate(startDate)}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
